import { useState } from "react";
import { C } from "../constants/colors";

const GRANTS = [
  "Creative Australia — Project Grants (Federal)",
  "Creative Victoria — Creative Projects",
  "Create NSW — Project Support",
  "Arts Queensland — Individual Artists Fund",
];

const GRANT_CRITERIA = {
  "Creative Australia — Project Grants (Federal)":
    "Assessment criteria: artistic merit and creative ambition; how the project advances the artist's practice; reach and engagement with audiences; feasibility and budget accountability.",
  "Creative Victoria — Creative Projects":
    "Assessment criteria: creative excellence and ambition; contribution to Victorian arts and culture; professional development outcomes; community and audience engagement; budget viability.",
  "Create NSW — Project Support":
    "Assessment criteria: artistic quality and innovation; development of the artist's practice; public benefit and access; NSW cultural significance; budget justification.",
  "Arts Queensland — Individual Artists Fund":
    "Assessment criteria: artistic merit; career development and professional growth; Queensland cultural contribution; project feasibility and budget.",
};

const SECTIONS = [
  {
    id: "practice",
    heading: "Your Practice",
    explanation:
      "Describe your artistic practice so the assessor understands who you are and what drives your work.",
    fields: [
      "Describe your artistic practice in your own words",
      "What medium(s) do you work in?",
      "What themes or ideas does your work explore?",
      "List your key career achievements (exhibitions, awards, residencies, publications)",
    ],
  },
  {
    id: "project",
    heading: "The Project",
    explanation:
      "Describe the specific project you're seeking funding for. Be concrete — assessors want to know exactly what you'll make and why.",
    fields: [
      "What is the project? Describe what you will create or do.",
      "Why do you want to make this project? What's the driving idea?",
      "What is the timeline? When will it happen?",
      "Where will it take place, and who are any key collaborators or partners?",
    ],
  },
  {
    id: "budget",
    heading: "Budget",
    explanation:
      "Grants require a clear budget. Describe how you'll use the funds and what other income or support you have.",
    fields: [
      "How much are you requesting, and what will the funds be spent on?",
      "Do you have any other funding, support in kind, or co-contributions?",
    ],
  },
  {
    id: "audience",
    heading: "Audience & Impact",
    explanation:
      "Assessors want to know who will experience this work and why it matters.",
    fields: [
      "Who is the audience for this project?",
      "How will audiences access or experience the work?",
      "What is the broader impact or significance of this project?",
    ],
  },
  {
    id: "career",
    heading: "Career Development",
    explanation:
      "Grants are investments in your career. Explain how this project advances your practice.",
    fields: [
      "How does this project develop your practice or career?",
      "What skills, relationships, or opportunities will it create for you?",
    ],
  },
];

const SEL = {
  width: "100%",
  padding: "12px 14px",
  border: `1.5px solid ${C.sand}`,
  borderRadius: 5,
  background: "#fff",
  fontSize: ".9rem",
  color: C.bark,
  outline: "none",
  minHeight: 48,
  appearance: "none",
  WebkitAppearance: "none",
};

export function GrantWriting({ profile }) {
  const [grant,        setGrant]        = useState("");
  const [answers,      setAnswers]      = useState({});
  const [saved,        setSaved]        = useState({});
  const [draftOutput,  setDraftOutput]  = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftError,   setDraftError]   = useState(null);
  const [draftCopied,  setDraftCopied]  = useState(false);

  const setAnswer = (sIdx, fIdx, val) =>
    setAnswers(prev => ({ ...prev, [sIdx]: { ...(prev[sIdx] || {}), [fIdx]: val } }));

  const saveSection = (sIdx) => {
    setSaved(s => ({ ...s, [sIdx]: true }));
    setTimeout(() => setSaved(s => ({ ...s, [sIdx]: false })), 2000);
  };

  const allFieldsFilled = () =>
    SECTIONS.every((section, sIdx) =>
      section.fields.every((_, fIdx) => (answers[sIdx] || {})[fIdx]?.trim())
    );

  const generateFullDraft = async () => {
    if (!allFieldsFilled()) {
      setDraftError("Please fill in all sections before generating your draft.");
      return;
    }

    setDraftLoading(true);
    setDraftError(null);

    const allContent = SECTIONS.map((section, sIdx) => {
      const sectionAnswers = answers[sIdx] || {};
      const fieldLines = section.fields
        .map((field, fIdx) => `${field}: ${sectionAnswers[fIdx] || "(not provided)"}`)
        .join("\n");
      return `## ${section.heading}\n${fieldLines}`;
    }).join("\n\n");

    const systemPrompt =
      `You are an expert Australian arts grant writer helping a visual artist apply for ${grant}. ` +
      `Write in clear, professional Australian English. Be specific and concrete. ` +
      `Do not use generic filler language. Write in first person as the artist. ` +
      `Output only the grant copy — no preamble, no explanation, no meta-commentary.`;

    const userPrompt =
      `Based on the following information, write a complete ${grant} application. ` +
      `Use the grant's assessment criteria and language. Include a clear heading for each section. ` +
      `Write each section as polished grant copy (2–4 paragraphs per section).\n\n` +
      `${allContent}\n\n` +
      `${GRANT_CRITERIA[grant]}`;

    try {
      const res = await fetch("/api/generate-grant", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          system: systemPrompt,
          messages: [{ role: "user", content: userPrompt }],
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(JSON.stringify(errData, null, 2) || `API error ${res.status}`);
      }

      const data = await res.json();
      setDraftOutput(data.content?.[0]?.text || "");
    } catch (err) {
      console.error("Grant Writing API error:", err);
      setDraftError(err.message);
    } finally {
      setDraftLoading(false);
    }
  };

  const copyDraft = () => {
    navigator.clipboard.writeText(draftOutput).catch(() => {});
    setDraftCopied(true);
    setTimeout(() => setDraftCopied(false), 2000);
  };

  // ── Locked state ──────────────────────────────────────────────────────────
  if (profile?.plan !== "professional") {
    return (
      <div className="empty-state">
        <div style={{ fontSize: "2.5rem", marginBottom: 14, opacity: .5 }}>🔒</div>
        <div style={{ fontFamily: "'Fraunces',serif", fontSize: "1.2rem", color: C.bark, marginBottom: 10 }}>
          Grant Writing Tool
        </div>
        <p style={{ fontSize: ".84rem", color: C.muted, maxWidth: 360, lineHeight: 1.7 }}>
          This tool is available on the Professional plan ($39/mo). Upgrade coming soon.
        </p>
      </div>
    );
  }

  // ── Tool ──────────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.7rem", fontWeight: 400, color: C.bark, marginBottom: 4 }}>
          Grant Writing
        </h1>
        <p style={{ fontSize: ".84rem", color: C.muted }}>
          AI-assisted drafts for Australian arts grant applications.
        </p>
      </div>

      {/* Grant selector */}
      <div className="form-group" style={{ marginBottom: 28 }}>
        <label className="form-label">Which grant are you applying for?</label>
        <div style={{ position: "relative", maxWidth: 580 }}>
          <select
            style={{ ...SEL, color: grant ? C.bark : C.muted }}
            value={grant}
            onChange={e => setGrant(e.target.value)}
          >
            <option value="" disabled>— Select a grant —</option>
            {GRANTS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: C.muted, fontSize: ".75rem" }}>▾</span>
        </div>
      </div>

      {/* Pre-selection prompt */}
      {!grant && (
        <div style={{ border: `2px dashed ${C.sand}`, borderRadius: 8, padding: "48px 20px", textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: 12, opacity: .4 }}>✍️</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: "1rem", color: C.bark }}>
            Select a grant above to get started.
          </div>
        </div>
      )}

      {/* Sections */}
      {grant && SECTIONS.map((section, sIdx) => {
        const isLast = sIdx === SECTIONS.length - 1;
        const isSaved = !!saved[sIdx];

        return (
          <div
            key={section.id}
            style={{
              background: C.parch,
              border: `1px solid ${C.parchD}`,
              borderRadius: 10,
              padding: 24,
              marginBottom: 20,
            }}
          >
            {/* Section label + heading */}
            <div style={{ marginBottom: 6 }}>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: ".56rem", letterSpacing: "2px", textTransform: "uppercase", color: C.muted }}>
                Section {sIdx + 1} of {SECTIONS.length}
              </span>
            </div>
            <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.1rem", fontWeight: 400, color: C.bark, marginBottom: 8 }}>
              {section.heading}
            </h2>
            <p style={{ fontSize: ".8rem", color: C.muted, lineHeight: 1.65, marginBottom: 16 }}>
              {section.explanation}
            </p>

            {/* Fields */}
            {section.fields.map((field, fIdx) => (
              <div key={fIdx} className="form-group">
                <label className="form-label">{field}</label>
                <textarea
                  className="form-input"
                  style={{ minHeight: 80, resize: "vertical" }}
                  placeholder="Your answer…"
                  value={(answers[sIdx] || {})[fIdx] || ""}
                  onChange={e => setAnswer(sIdx, fIdx, e.target.value)}
                />
              </div>
            ))}

            {/* Sections 1–4: Save button */}
            {!isLast && (
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 16 }}>
                {isSaved && (
                  <span style={{ fontSize: ".75rem", color: C.sage }}>Saved ✓</span>
                )}
                <button
                  className="btn-g"
                  style={{ fontSize: ".68rem", padding: "10px 20px", minHeight: 42 }}
                  onClick={() => saveSection(sIdx)}
                >
                  Save
                </button>
              </div>
            )}

            {/* Section 5: Generate full draft button */}
            {isLast && (
              <div style={{ marginTop: 20 }}>
                {draftError && !draftOutput && (
                  <div style={{ background: C.roseL, border: `1px solid ${C.rose}`, borderRadius: 5, padding: "10px 14px", marginBottom: 14, fontSize: ".8rem", color: C.rose, lineHeight: 1.5 }}>
                    ⚠ {draftError}
                  </div>
                )}
                <button
                  className="btn-p"
                  style={{ width: "100%", fontSize: ".75rem", padding: "14px", minHeight: 50 }}
                  onClick={generateFullDraft}
                  disabled={draftLoading}
                >
                  {draftLoading ? "Generating…" : "Generate full draft ↗"}
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Output box — shown below section 5 once draft is ready */}
      {draftOutput && (
        <div style={{ marginTop: 8, marginBottom: 40 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: ".56rem", letterSpacing: "2px", textTransform: "uppercase", color: C.muted }}>
              Generated draft
            </span>
            <button
              className="btn-g"
              style={{ fontSize: ".62rem", padding: "5px 13px", minHeight: 32 }}
              onClick={copyDraft}
            >
              {draftCopied ? "Copied ✓" : "Copy"}
            </button>
          </div>
          <div style={{
            background: C.cream,
            border: `1px solid ${C.sand}`,
            borderRadius: 8,
            padding: 20,
            fontFamily: "'DM Sans',sans-serif",
            fontSize: ".875rem",
            color: C.bark,
            lineHeight: 1.7,
            whiteSpace: "pre-wrap",
          }}>
            {draftOutput}
          </div>
          {draftError && (
            <div style={{ background: C.roseL, border: `1px solid ${C.rose}`, borderRadius: 5, padding: "10px 14px", marginTop: 14, fontSize: ".8rem", color: C.rose, lineHeight: 1.5 }}>
              ⚠ {draftError}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button
              className="btn-g"
              style={{ fontSize: ".68rem", padding: "8px 16px", minHeight: 38 }}
              onClick={generateFullDraft}
              disabled={draftLoading}
            >
              {draftLoading ? "Generating…" : "Regenerate ↗"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
